/* ═══════════════════════════════════════════════════════════════
   filterModel — the v2 table filter state: typed rules, one level of
   OR/AND groups, migration from the legacy per-column format, and the
   single predicate every surface evaluates rows with.

   Shape (v2):
     { version: 2, match: 'all' | 'any', rules: (FilterRule | FilterGroup)[] }

   A FilterGroup carries its own match and its own flat rule list — one
   level deep, no nesting beyond that. The Filter Builder UI cannot yet
   CREATE groups (round 2), but state, predicate and chips already handle
   them so a group-bearing saved view drawn from any source just works.

   Zero imports on purpose: DataTable and FilterBuilder both depend on
   this module, so it depends on neither.
   ═══════════════════════════════════════════════════════════════ */

export type MatchMode = 'all' | 'any';

export type FilterOp =
  | 'contains'
  | 'notContains'
  | 'is'
  | 'isNot'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'anyOf'
  | 'noneOf'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'between'
  | 'after'
  | 'before'
  | 'betweenDates'
  | 'inNext'
  /** Widgets R2 Builder R2 — relative-date presets, evaluated against
   *  `now` (a param on `matchesFilterState`/`evalRule`, defaulting to
   *  `new Date()`) so a saved widget/view stays current on every render.
   *  `overdue` here is the date half only ("before today"); a caller with
   *  row/source context (widgetEvaluator) may layer an additional "AND
   *  not complete" check on top — this module has no notion of what
   *  "complete" means for an arbitrary table. */
  | 'today'
  | 'next7'
  | 'next30'
  | 'overdue'
  | 'thisMonth';

export interface FilterRule {
  field: string;
  op: FilterOp;
  /** Shape depends on op:
   *  contains/notContains/is/isNot → string
   *  anyOf/noneOf                  → string[]
   *  eq/neq/gt/lt                  → number
   *  between                       → { min?: number; max?: number }
   *  after/before                  → string (yyyy-mm-dd)
   *  betweenDates                  → { min?: string; max?: string }
   *  inNext                        → { days: number }
   *  isEmpty/isNotEmpty            → undefined */
  value?: unknown;
}

export interface FilterGroup {
  match: MatchMode;
  rules: FilterRule[];
}

export interface TableFilterState {
  version: 2;
  match: MatchMode;
  rules: (FilterRule | FilterGroup)[];
}

/** Column filter flavors the table auto-detects — mirrors DataTable's
 *  `ColumnFilterType` without importing it (keeps this module dependency-free). */
export type ColumnFilterKindSource = 'multiselect' | 'text' | 'number-range' | 'date-range';

/** The four kinds the builder shows controls for. */
export type FilterFieldKind = 'text' | 'multiselect' | 'number' | 'date';

export const EMPTY_FILTER_STATE: TableFilterState = { version: 2, match: 'all', rules: [] };

export function isFilterGroup(r: FilterRule | FilterGroup): r is FilterGroup {
  return !!r && Array.isArray((r as FilterGroup).rules);
}

export function fieldKind(t: ColumnFilterKindSource): FilterFieldKind {
  if (t === 'number-range') return 'number';
  if (t === 'date-range') return 'date';
  if (t === 'multiselect') return 'multiselect';
  return 'text';
}

// ─── Operator catalog ───

export const OPS_BY_KIND: Record<FilterFieldKind, FilterOp[]> = {
  text: ['contains', 'notContains', 'is', 'isNot', 'isEmpty', 'isNotEmpty'],
  multiselect: ['anyOf', 'noneOf', 'isEmpty', 'isNotEmpty'],
  number: ['eq', 'neq', 'gt', 'lt', 'between', 'isEmpty', 'isNotEmpty'],
  date: [
    'betweenDates',
    'after',
    'before',
    'inNext',
    'today',
    'next7',
    'next30',
    'overdue',
    'thisMonth',
    'isEmpty',
    'isNotEmpty',
  ],
};

const OP_LABELS: Record<FilterOp, string> = {
  contains: 'Contains',
  notContains: "Doesn't contain",
  is: 'Is',
  isNot: 'Is not',
  isEmpty: 'Is empty',
  isNotEmpty: 'Is not empty',
  anyOf: 'Is any of',
  noneOf: 'Is none of',
  eq: 'Equals',
  neq: "Doesn't equal",
  gt: 'Greater than',
  lt: 'Less than',
  between: 'Between',
  after: 'On or after',
  before: 'On or before',
  betweenDates: 'Between dates',
  inNext: 'In the next',
  today: 'Today',
  next7: 'Next 7 days',
  next30: 'Next 30 days',
  overdue: 'Overdue',
  thisMonth: 'This month',
};

export function opLabel(op: FilterOp): string {
  return OP_LABELS[op] || op;
}

export function defaultOpFor(kind: FilterFieldKind): FilterOp {
  if (kind === 'multiselect') return 'anyOf';
  if (kind === 'number') return 'eq';
  if (kind === 'date') return 'betweenDates';
  return 'contains';
}

/** A fresh value slot for an operator — keeps editors controlled. */
export function defaultValueFor(op: FilterOp): unknown {
  switch (op) {
    case 'anyOf':
    case 'noneOf':
      return [];
    case 'between':
      return { min: undefined, max: undefined };
    case 'betweenDates':
      return { min: undefined, max: undefined };
    case 'inNext':
      return { days: 7 };
    case 'isEmpty':
    case 'isNotEmpty':
    case 'today':
    case 'next7':
    case 'next30':
    case 'overdue':
    case 'thisMonth':
      return undefined;
    case 'eq':
    case 'neq':
    case 'gt':
    case 'lt':
      return undefined;
    default:
      return '';
  }
}

// ─── Activity ───

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map(str).filter((s) => s !== '') : [];
}

function num(v: unknown): number | undefined {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** A rule narrows rows only once it carries a usable value. Half-typed
 *  rules stay in the draft but never hide data. */
export function isRuleActive(r: FilterRule): boolean {
  if (!r || !r.field) return false;
  switch (r.op) {
    case 'isEmpty':
    case 'isNotEmpty':
    case 'today':
    case 'next7':
    case 'next30':
    case 'overdue':
    case 'thisMonth':
      return true;
    case 'anyOf':
    case 'noneOf':
      return strArray(r.value).length > 0;
    case 'between': {
      const v = (r.value || {}) as { min?: unknown; max?: unknown };
      return num(v.min) != null || num(v.max) != null;
    }
    case 'betweenDates': {
      const v = (r.value || {}) as { min?: unknown; max?: unknown };
      return !!str(v.min) || !!str(v.max);
    }
    case 'inNext':
      return num((r.value as { days?: unknown })?.days) != null;
    case 'eq':
    case 'neq':
    case 'gt':
    case 'lt':
      return num(r.value) != null;
    default:
      return str(r.value).trim() !== '';
  }
}

export function activeRuleCount(state: TableFilterState | null | undefined): number {
  if (!state) return 0;
  let n = 0;
  for (const r of state.rules) {
    if (isFilterGroup(r)) n += r.rules.filter(isRuleActive).length ? 1 : 0;
    else if (isRuleActive(r)) n += 1;
  }
  return n;
}

export function hasActiveFilters(state: TableFilterState | null | undefined): boolean {
  return activeRuleCount(state) > 0;
}

/** Drops half-typed rules (and emptied groups) — used when committing. */
export function pruneInactive(state: TableFilterState): TableFilterState {
  const rules: (FilterRule | FilterGroup)[] = [];
  for (const r of state.rules) {
    if (isFilterGroup(r)) {
      const kept = r.rules.filter(isRuleActive);
      if (kept.length > 0) rules.push({ match: r.match, rules: kept });
    } else if (isRuleActive(r)) {
      rules.push(r);
    }
  }
  return { version: 2, match: state.match, rules };
}

// ─── Legacy migration ───
// Old format: Record<columnKey, { type: 'multiselect'|'text'|'number-range'|
// 'date-range', … }>. Read from localStorage (`dt_filters_*`), the per-user
// Prefs mirror, and DashboardView config blobs — all three funnel here so no
// saved filter or saved view ever stops working.

function migrateLegacyValue(field: string, fv: any): FilterRule | null {
  if (!fv || typeof fv !== 'object') return null;
  if (fv.type === 'multiselect') {
    const values = strArray(fv.values);
    return values.length ? { field, op: 'anyOf', value: values } : null;
  }
  if (fv.type === 'text') {
    const s = str(fv.contains).trim();
    return s ? { field, op: 'contains', value: s } : null;
  }
  if (fv.type === 'number-range') {
    const min = num(fv.min);
    const max = num(fv.max);
    if (min == null && max == null) return null;
    return { field, op: 'between', value: { min, max } };
  }
  if (fv.type === 'date-range') {
    const min = str(fv.min) || undefined;
    const max = str(fv.max) || undefined;
    if (!min && !max) return null;
    return { field, op: 'betweenDates', value: { min, max } };
  }
  return null;
}

function sanitizeRule(r: any): FilterRule | null {
  if (!r || typeof r !== 'object' || typeof r.field !== 'string' || !r.field) return null;
  if (typeof r.op !== 'string' || !(r.op in OP_LABELS)) return null;
  return { field: r.field, op: r.op as FilterOp, value: r.value };
}

/** Accepts v2 state, the legacy per-column record, or junk — always
 *  returns valid v2 state. */
export function migrateFilterState(raw: unknown): TableFilterState {
  if (!raw || typeof raw !== 'object') return { version: 2, match: 'all', rules: [] };
  const any = raw as any;

  // Already v2 (or v2-shaped) — sanitize and pass through.
  if (any.version === 2 || Array.isArray(any.rules)) {
    const rules: (FilterRule | FilterGroup)[] = [];
    for (const r of Array.isArray(any.rules) ? any.rules : []) {
      if (r && Array.isArray(r.rules)) {
        const inner = r.rules.map(sanitizeRule).filter(Boolean) as FilterRule[];
        if (inner.length) rules.push({ match: r.match === 'any' ? 'any' : 'all', rules: inner });
      } else {
        const s = sanitizeRule(r);
        if (s) rules.push(s);
      }
    }
    return { version: 2, match: any.match === 'any' ? 'any' : 'all', rules };
  }

  // Legacy per-column record: { [colKey]: ColumnFilterValue }
  const rules: (FilterRule | FilterGroup)[] = [];
  for (const key of Object.keys(any)) {
    const rule = migrateLegacyValue(key, any[key]);
    if (rule) rules.push(rule);
  }
  return { version: 2, match: 'all', rules };
}

// ─── Predicate ───

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Date-only strings ("2026-08-24") parse as UTC midnight in JS, which is the
 *  PREVIOUS day in any western timezone — so a relative window like "in the
 *  next week" would miss today. Parse those as LOCAL midnight; anything else
 *  falls back to the platform parser. */
function parseDateValue(v: string): number {
  const m = DATE_ONLY.exec(v.trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  return Date.parse(v);
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function endOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

/** End-of-day, `days` CALENDAR days after `now`'s local date — never
 *  `now.getTime() + days*86400000`, which assumes every day is exactly
 *  24h. A DST transition inside the window makes that ms-based math land
 *  on the wrong calendar day (off by one, either direction) for a
 *  same-week `next7`. Stepping the date fields (`d + days`) and letting
 *  `Date` normalize is DST-proof. */
function endOfNextDays(now: Date, days: number): number {
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + days,
    23,
    59,
    59,
    999
  ).getTime();
}

/** Evaluates ONE rule against the row's display value for that field.
 *  `raw === undefined` means the field doesn't exist on this table — the
 *  rule goes inert instead of blanking the grid. `now` grounds the
 *  relative-date ops (today/next7/next30/overdue/thisMonth) — defaults to
 *  the real clock so every existing caller is unaffected; tests inject a
 *  fixed instant. */
export function evalRule(
  raw: string | number | string[] | null | undefined,
  rule: FilterRule,
  now: Date = new Date()
): boolean {
  // Relative-date ops read THIS ROW's value, not the table's schema — an
  // undefined/null/empty date here means "this row has no date," which
  // must fail today/next7/next30/overdue/thisMonth, never go inert. (The
  // generic `raw === undefined` → inert rule below is for a genuinely
  // missing COLUMN, e.g. a field this table doesn't have; it must not
  // also swallow a present-but-empty date on an otherwise-valid row.)
  const isRelativeDateOp =
    rule.op === 'today' ||
    rule.op === 'next7' ||
    rule.op === 'next30' ||
    rule.op === 'overdue' ||
    rule.op === 'thisMonth';
  if (
    isRelativeDateOp &&
    isRuleActive(rule) &&
    (raw === undefined || raw === null || str(raw).trim() === '')
  ) {
    return false;
  }
  if (raw === undefined) return true;
  if (!isRuleActive(rule)) return true;

  // Multi-valued field (a row that carries SEVERAL values for one field —
  // e.g. a task's several linked projects or assignees). Only anyOf/noneOf/
  // empty have a defined "which of several" meaning; every other op falls
  // through to the joined-string behavior below via `str()`.
  if (Array.isArray(raw)) {
    if (rule.op === 'anyOf') return strArray(rule.value).some((v) => raw.includes(v));
    if (rule.op === 'noneOf') return !strArray(rule.value).some((v) => raw.includes(v));
    if (rule.op === 'isEmpty') return raw.length === 0;
    if (rule.op === 'isNotEmpty') return raw.length > 0;
  }

  const s = str(raw);
  const lower = s.toLowerCase();

  switch (rule.op) {
    case 'isEmpty':
      return s.trim() === '';
    case 'isNotEmpty':
      return s.trim() !== '';
    case 'contains':
      return lower.includes(str(rule.value).toLowerCase());
    case 'notContains':
      return !lower.includes(str(rule.value).toLowerCase());
    case 'is':
      return lower === str(rule.value).toLowerCase();
    case 'isNot':
      return lower !== str(rule.value).toLowerCase();
    case 'anyOf':
      return strArray(rule.value).includes(s);
    case 'noneOf':
      return !strArray(rule.value).includes(s);
    case 'eq':
    case 'neq':
    case 'gt':
    case 'lt': {
      const n = Number(s);
      const target = num(rule.value);
      if (!Number.isFinite(n) || target == null) return false;
      if (rule.op === 'eq') return n === target;
      if (rule.op === 'neq') return n !== target;
      if (rule.op === 'gt') return n > target;
      return n < target;
    }
    case 'between': {
      const n = Number(s);
      if (!Number.isFinite(n)) return false;
      const v = (rule.value || {}) as { min?: unknown; max?: unknown };
      const min = num(v.min);
      const max = num(v.max);
      if (min != null && n < min) return false;
      if (max != null && n > max) return false;
      return true;
    }
    case 'after':
    case 'before': {
      const t = parseDateValue(s);
      const b = parseDateValue(str(rule.value));
      if (isNaN(t) || isNaN(b)) return false;
      return rule.op === 'after' ? t >= startOfDay(b) : t <= endOfDay(b);
    }
    case 'betweenDates': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      const v = (rule.value || {}) as { min?: unknown; max?: unknown };
      const minS = str(v.min);
      const maxS = str(v.max);
      if (minS) {
        const b = parseDateValue(minS);
        if (!isNaN(b) && t < startOfDay(b)) return false;
      }
      if (maxS) {
        const b = parseDateValue(maxS);
        if (!isNaN(b) && t > endOfDay(b)) return false;
      }
      return true;
    }
    case 'inNext': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      const days = num((rule.value as { days?: unknown })?.days);
      if (days == null) return true;
      return t >= startOfDay(now.getTime()) && t <= endOfNextDays(now, days);
    }
    case 'today': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      const nowMs = now.getTime();
      return t >= startOfDay(nowMs) && t <= endOfDay(nowMs);
    }
    case 'next7':
    case 'next30': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      const days = rule.op === 'next7' ? 7 : 30;
      return t >= startOfDay(now.getTime()) && t <= endOfNextDays(now, days);
    }
    case 'overdue': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      return t < startOfDay(now.getTime());
    }
    case 'thisMonth': {
      const t = parseDateValue(s);
      if (isNaN(t)) return false;
      const d = new Date(t);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }
    default:
      return true;
  }
}

/** Whole-state predicate. `resolve(field)` returns the row's display value
 *  for a field, or `undefined` when the table has no such column. */
export function matchesFilterState(
  state: TableFilterState | null | undefined,
  resolve: (field: string) => string | number | string[] | null | undefined,
  now: Date = new Date()
): boolean {
  if (!state || state.rules.length === 0) return true;
  const nodes = state.rules.filter((r) =>
    isFilterGroup(r) ? r.rules.some(isRuleActive) : isRuleActive(r)
  );
  if (nodes.length === 0) return true;

  const evalNode = (node: FilterRule | FilterGroup): boolean => {
    if (isFilterGroup(node)) {
      const active = node.rules.filter(isRuleActive);
      if (active.length === 0) return true;
      return node.match === 'any'
        ? active.some((r) => evalRule(resolve(r.field), r, now))
        : active.every((r) => evalRule(resolve(r.field), r, now));
    }
    return evalRule(resolve(node.field), node, now);
  };

  return state.match === 'any' ? nodes.some(evalNode) : nodes.every(evalNode);
}

// ─── Summaries (chips) ───

const NEXT_PRESETS: { days: number; label: string }[] = [
  { days: 7, label: 'next week' },
  { days: 14, label: 'next 2 weeks' },
  { days: 30, label: 'next month' },
];

export function nextPresetLabel(days: number): string {
  return NEXT_PRESETS.find((p) => p.days === days)?.label || `next ${days} days`;
}

export const QUICK_DATE_PRESETS = NEXT_PRESETS;

/** Compact "op + value" text for a chip — the field label is rendered
 *  separately so it can carry its own weight. */
export function ruleValueText(
  rule: FilterRule,
  /** Maps a stored list value to the label the user picked (e.g. active → Active). */
  valueLabel: (v: string) => string = (v) => v
): string {
  switch (rule.op) {
    case 'isEmpty':
      return 'is empty';
    case 'isNotEmpty':
      return 'is not empty';
    case 'contains':
      return `contains "${str(rule.value)}"`;
    case 'notContains':
      return `excludes "${str(rule.value)}"`;
    case 'is':
      return `is "${str(rule.value)}"`;
    case 'isNot':
      return `is not "${str(rule.value)}"`;
    case 'anyOf': {
      const v = strArray(rule.value);
      return v.length === 1 ? `is ${valueLabel(v[0])}` : `is any of ${v.length}`;
    }
    case 'noneOf': {
      const v = strArray(rule.value);
      return v.length === 1 ? `is not ${valueLabel(v[0])}` : `is none of ${v.length}`;
    }
    case 'eq':
      return `= ${str(rule.value)}`;
    case 'neq':
      return `≠ ${str(rule.value)}`;
    case 'gt':
      return `> ${str(rule.value)}`;
    case 'lt':
      return `< ${str(rule.value)}`;
    case 'between': {
      const v = (rule.value || {}) as { min?: unknown; max?: unknown };
      const min = num(v.min);
      const max = num(v.max);
      if (min != null && max != null) return `${min}–${max}`;
      if (min != null) return `≥ ${min}`;
      return `≤ ${max}`;
    }
    case 'after':
      return `on or after ${str(rule.value)}`;
    case 'before':
      return `on or before ${str(rule.value)}`;
    case 'betweenDates': {
      const v = (rule.value || {}) as { min?: unknown; max?: unknown };
      const min = str(v.min);
      const max = str(v.max);
      if (min && max) return `${min} – ${max}`;
      if (min) return `from ${min}`;
      return `until ${max}`;
    }
    case 'inNext': {
      const days = num((rule.value as { days?: unknown })?.days) ?? 0;
      return `in the ${nextPresetLabel(days)}`;
    }
    case 'today':
      return 'is today';
    case 'next7':
      return 'in the next 7 days';
    case 'next30':
      return 'in the next 30 days';
    case 'overdue':
      return 'is overdue';
    case 'thisMonth':
      return 'is this month';
    default:
      return '';
  }
}

/** Read-only one-line summary of a group chip (round-2 states display today). */
export function groupSummaryText(
  group: FilterGroup,
  labelOf: (field: string) => string,
  valueLabel?: (field: string, v: string) => string
): string {
  const joiner = group.match === 'any' ? ' or ' : ' and ';
  return group.rules
    .filter(isRuleActive)
    .map(
      (r) =>
        `${labelOf(r.field)} ${ruleValueText(r, (v) => (valueLabel ? valueLabel(r.field, v) : v))}`
    )
    .join(joiner);
}
